import {HubConnection, HubConnectionBuilder} from "@microsoft/signalr";

interface HubMessageRegistration {
    callbacks: Record<string, (message: any) => void>;
    handler: (message: any) => void;
    lastMessage?: any;
}

interface HubRegistry {
    at: string;
    hubUrl: string;
    connection: HubConnection;
    connected: boolean;
    connectionCallbacks: Record<string, (connected: boolean) => void>;
    messages: Record<string, HubMessageRegistration>;
    reconnectTimeoutId?: number;
    startPromise?: Promise<void>;
    subscriberCount: number;
}

const hubs: Record<string, HubRegistry> = {};
const debug = false;

const buildHubUrl = (at: string): string => {
    if (/^https?:\/\//i.test(at)) {
        return at;
    }

    const normalizedPath = at.startsWith("/") ? at : `/${at}`;

    let proxyBase = "";
    try {
        proxyBase =
            (typeof localStorage !== "undefined" && localStorage.getItem("useProxy")) ||
            "";
    } catch {
        proxyBase = "";
    }

    if (!proxyBase) {
        return normalizedPath;
    }

    const trimmedBase = proxyBase.endsWith("/") ? proxyBase.slice(0, -1) : proxyBase;
    return `${trimmedBase}${normalizedPath}`;
};

const parseMessage = (message: any) => {
    try {
        return typeof message === "string" ? JSON.parse(message) : message;
    } catch (e) {
        console.log("Error parsing message", e);
        return message;
    }
};

const notifyConnection = (registry: HubRegistry, connected: boolean) => {
    Object.values(registry.connectionCallbacks).forEach((callback) => {
        try {
            callback(connected);
        } catch {
        }
    });
};

const scheduleReconnect = (hubKey: string) => {
    const registry = hubs[hubKey];
    if (!registry || registry.reconnectTimeoutId || registry.subscriberCount === 0) {
        return;
    }

    registry.reconnectTimeoutId = window.setTimeout(() => {
        const current = hubs[hubKey];
        if (!current) {
            return;
        }

        current.reconnectTimeoutId = undefined;
        void startHub(hubKey);
    }, 3000);
};

const stopHub = async (hubKey: string) => {
    const registry = hubs[hubKey];
    if (!registry) {
        return;
    }

    if (registry.reconnectTimeoutId) {
        window.clearTimeout(registry.reconnectTimeoutId);
        registry.reconnectTimeoutId = undefined;
    }

    delete hubs[hubKey];
    registry.connected = false;

    try {
        await registry.connection.stop();
    } catch (e) {
        console.log("Error stopping hub", e);
    }
};

const startHub = (hubKey: string): Promise<void> => {
    const registry = hubs[hubKey];
    if (!registry) {
        return Promise.resolve();
    }

    if (registry.connected) {
        return Promise.resolve();
    }

    if (registry.startPromise) {
        return registry.startPromise;
    }

    if (registry.reconnectTimeoutId) {
        window.clearTimeout(registry.reconnectTimeoutId);
        registry.reconnectTimeoutId = undefined;
    }

    registry.startPromise = registry.connection
        .start()
        .then(() => {
            const current = hubs[hubKey];
            if (!current) {
                return;
            }

            current.connected = true;
            notifyConnection(current, true);

            Object.keys(current.messages).forEach((messageType) => {
                current.connection.invoke("Subscribe", messageType).catch(() => {
                });
            });
        })
        .catch(() => {
            const current = hubs[hubKey];
            if (!current) {
                return;
            }

            current.connected = false;
            notifyConnection(current, false);
            scheduleReconnect(hubKey);
        })
        .finally(() => {
            const current = hubs[hubKey];
            if (current) {
                current.startPromise = undefined;
            }
        });

    return registry.startPromise;
};

const ensureHub = (at: string): [string, HubRegistry] => {
    const hubUrl = buildHubUrl(at);
    const hubKey = `AT:${hubUrl}`;
    const existing = hubs[hubKey];
    if (existing) {
        return [hubKey, existing];
    }

    const connection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
            accessTokenFactory(): string | Promise<string> {
                try {
                    return localStorage.getItem("auth_token") ?? "";
                } catch {
                    return "";
                }
            },
        })
        .build();

    const registry: HubRegistry = {
        at,
        hubUrl,
        connection,
        connected: false,
        connectionCallbacks: {},
        messages: {},
        subscriberCount: 0,
    };

    connection.onclose(() => {
        const current = hubs[hubKey];
        if (!current) {
            return;
        }

        current.connected = false;
        notifyConnection(current, false);
        scheduleReconnect(hubKey);
    });

    hubs[hubKey] = registry;
    void startHub(hubKey);

    return [hubKey, registry];
};

export function MakeHub<T>(
    at: string,
    messageType: string,
    onMessage: (message: T) => void,
    connectionChangeCallback: ((change: boolean) => void) | null = null,
    isGlobal: boolean,
): HubEntry {
    const [hubKey, registry] = ensureHub(at);
    const subscriptionId = `${messageType}:${Math.random().toString(36).slice(2)}`;

    registry.subscriberCount += 1;

    if (connectionChangeCallback) {
        registry.connectionCallbacks[subscriptionId] = connectionChangeCallback;
        if (registry.connected) {
            // Defer replay so callers can safely reference the returned HubEntry.
            Promise.resolve().then(() => {
                const current = hubs[hubKey];
                const callback = current?.connectionCallbacks[subscriptionId];
                if (current?.connected && callback) {
                    callback(true);
                }
            });
        }
    }

    if (!registry.messages[messageType]) {
        registry.messages[messageType] = {
            callbacks: {},
            handler: (message) => {
                const parsed = parseMessage(message);
                const current = hubs[hubKey];
                if (!current) {
                    return;
                }

                current.messages[messageType].lastMessage = parsed;
                Object.values(current.messages[messageType].callbacks).forEach((callback) => {
                    try {
                        callback(parsed);
                    } catch {
                    }
                });
            },
        };

        if (debug) {
            console.log("Registering new hub handler", hubKey, messageType);
        }

        registry.connection.on(messageType, registry.messages[messageType].handler);
    }

    registry.messages[messageType].callbacks[subscriptionId] = onMessage as (message: any) => void;

    if (registry.messages[messageType].lastMessage !== undefined) {
        onMessage(registry.messages[messageType].lastMessage as T);
    }

    if (registry.connected) {
        registry.connection.invoke("Subscribe", messageType).catch(() => {
        });
    }

    return new HubEntry(
        registry.connection,
        hubKey,
        messageType,
        subscriptionId,
        isGlobal,
    );
}

export class HubEntry {
    private closed = false;

    constructor(
        public hub: HubConnection,
        private readonly hubKey: string,
        private readonly messageType: string,
        private readonly subscriptionId: string,
        public isGlobal: boolean,
    ) {
    }

    public sendMessage(messageType: string, message: any) {
        this.hub.invoke(messageType, message);
    }

    public close() {
        if (this.isGlobal || this.closed) {
            return;
        }

        this.closed = true;
        const registry = hubs[this.hubKey];
        if (!registry) {
            return;
        }

        delete registry.connectionCallbacks[this.subscriptionId];

        const messageRegistration = registry.messages[this.messageType];
        if (messageRegistration) {
            delete messageRegistration.callbacks[this.subscriptionId];
            if (Object.keys(messageRegistration.callbacks).length === 0) {
                registry.connection.off(this.messageType, messageRegistration.handler);
                delete registry.messages[this.messageType];
            }
        }

        registry.subscriberCount = Math.max(0, registry.subscriberCount - 1);
        if (registry.subscriberCount === 0) {
            void stopHub(this.hubKey);
        }
    }
}
